import type { UpdateFlagInput } from './flags';
import type { FlagDefinition, FlagLifecycle } from '../schemas/flag';

/** What the edit form holds — every field as typed, untrimmed. */
export interface DefinitionDraft {
    readonly name: string;
    readonly description: string;
    readonly category: string;
    readonly owner: string;
    readonly lifecycle: FlagLifecycle;
    /** Ignored unless the flag is a `salesforce` flag. */
    readonly salesforceField: string;
}

export function definitionDraftFrom(definition: FlagDefinition): DefinitionDraft {
    return {
        name: definition.name,
        description: definition.description,
        category: definition.category,
        owner: definition.owner,
        lifecycle: definition.lifecycle,
        salesforceField: definition.salesforceField ?? '',
    };
}

/**
 * Build a `PATCH /flags/:key` body from the edit form: only the fields that
 * changed, trimmed the way the API trims them. `null` when nothing did.
 *
 * ## Why only the changed fields
 *
 * Every accepted edit bumps the version and files an audit entry that names the
 * fields it touched. Sending the whole form would record "Edited name,
 * description, category, …" for a one-word owner fix, in the one log operators
 * read to find what actually changed — and the API refuses a body with nothing
 * in it, which is what `null` lets the form avoid sending.
 *
 * ## Why `type` is never in it
 *
 * The type is immutable. The form shows it read-only; a body that carried it
 * would be a no-op at best, and a `FLAG_TYPE_IMMUTABLE` 409 at worst. A
 * `salesforceField` is sent only for a `salesforce` flag for the same kind of
 * reason: on a `veevart2` flag the API refuses one outright.
 */
export function definitionEditFrom(
    current: FlagDefinition,
    draft: DefinitionDraft,
    reason?: string,
): UpdateFlagInput | null {
    const name = draft.name.trim();
    const description = draft.description.trim();
    const category = draft.category.trim();
    const owner = draft.owner.trim();
    const salesforceField = draft.salesforceField.trim();

    const edit = {
        ...(name !== current.name ? { name } : {}),
        ...(description !== current.description ? { description } : {}),
        ...(category !== current.category ? { category } : {}),
        ...(owner !== current.owner ? { owner } : {}),
        ...(draft.lifecycle !== current.lifecycle ? { lifecycle: draft.lifecycle } : {}),
        ...(current.type === 'salesforce' && salesforceField !== current.salesforceField
            ? { salesforceField }
            : {}),
    };

    if (Object.keys(edit).length === 0) return null;

    return {
        ...edit,
        // The version the form was opened from — a colleague's edit in between
        // is a 409 the operator sees, not a silent overwrite.
        expectedVersion: current.version,
        ...(reason !== undefined ? { reason } : {}),
    };
}
